import React, { useEffect, useMemo, useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from "@stripe/react-stripe-js";
import toast from "react-hot-toast";
import { checkoutSession, paymentSession } from "../services/api";

interface StripePaymentProps {
  pricing: number;
  restaurantId: string;
  publishableKey: string;
  handleOrder: (paymentMethod: string, transactionId?: string) => void;
}

/** Stripe embedded checkout using the restaurant's own Stripe account. */
export default function StripePayment({
  pricing,
  restaurantId,
  publishableKey,
  handleOrder,
}: StripePaymentProps) {
  const stripePromise = useMemo(() => loadStripe(publishableKey), [publishableKey]);
  const [session, setSession] = useState<{ clientSecret: string; sessionId: string } | null>(null);

  useEffect(() => {
    let cancelled = false;
    checkoutSession({ amount: pricing, productName: "Order Payment", hotelId: restaurantId })
      .then((res) => {
        if (!cancelled) setSession(res.data);
      })
      .catch((err) => toast.error(err.response?.data?.error || "Could not start Stripe checkout"));
    return () => {
      cancelled = true;
    };
  }, [pricing, restaurantId]);

  const onComplete = async () => {
    if (!session) return;
    try {
      const { data } = await paymentSession(session.sessionId, restaurantId);
      const paid = data?.session?.payment_status === "paid";
      if (!paid) {
        toast.error("Payment was not completed");
        return;
      }
      toast.success("Payment successful!");
      handleOrder("stripe", data.session.payment_intent);
    } catch (err: any) {
      toast.error(err.response?.data?.error || "Could not confirm payment");
    }
  };

  if (!session) {
    return <p className="p-4 text-center text-gray-500">Loading payment form...</p>;
  }

  return (
    <EmbeddedCheckoutProvider
      stripe={stripePromise}
      options={{ fetchClientSecret: () => Promise.resolve(session.clientSecret), onComplete }}
    >
      <EmbeddedCheckout className="w-full" />
    </EmbeddedCheckoutProvider>
  );
}
